import type { CreateItem, Item, ListResponse, Template } from '@streepaf/shared'
import { api, type RequestOptions } from './client.js'

type ItemsResult = { items: Item[]; version: number }
type ItemResult = { item: Item; version: number }
type DeletedResult = { deletedIds: string[]; version: number }

export const listsApi = {
  create(body: Record<string, unknown>, options?: RequestOptions): Promise<ListResponse> {
    return api<ListResponse>('/lists', { ...options, method: 'POST', body })
  },

  get(token: string, options?: RequestOptions): Promise<ListResponse> {
    return api<ListResponse>(`/lists/${token}`, options)
  },

  update(token: string, patch: Record<string, unknown>): Promise<ListResponse> {
    return api<ListResponse>(`/lists/${token}`, { method: 'PATCH', body: patch })
  },

  remove(token: string): Promise<void> {
    return api<void>(`/lists/${token}`, { method: 'DELETE' })
  },

  duplicate(token: string, body: Record<string, unknown> = {}): Promise<ListResponse> {
    return api<ListResponse>(`/lists/${token}/duplicate`, { method: 'POST', body })
  },

  rotate(
    token: string,
    tokens: ('view' | 'edit')[],
  ): Promise<{
    tokens: { view?: string; edit?: string }
    links: { view?: string; edit?: string }
  }> {
    return api(`/lists/${token}/rotate`, { method: 'POST', body: { tokens } })
  },

  addItems(
    token: string,
    body: { items: CreateItem[]; afterId?: string | null; beforeId?: string },
    options?: RequestOptions,
  ): Promise<ItemsResult> {
    return api<ItemsResult>(`/lists/${token}/items`, { ...options, method: 'POST', body })
  },

  updateItem(token: string, itemId: string, patch: Record<string, unknown>): Promise<ItemResult> {
    return api<ItemResult>(`/lists/${token}/items/${itemId}`, { method: 'PATCH', body: patch })
  },

  deleteItem(token: string, itemId: string, cascade = false): Promise<DeletedResult> {
    const query = cascade ? '?deleteSectionItems=true' : ''
    return api<DeletedResult>(`/lists/${token}/items/${itemId}${query}`, { method: 'DELETE' })
  },

  moveItem(
    token: string,
    itemId: string,
    anchor: { afterId?: string | null; beforeId?: string },
  ): Promise<ItemResult> {
    return api<ItemResult>(`/lists/${token}/items/${itemId}/move`, { method: 'POST', body: anchor })
  },

  bulkCheck(token: string, ids: string[], checked: boolean, by?: string): Promise<ItemsResult> {
    return api<ItemsResult>(`/lists/${token}/items/check`, {
      method: 'POST',
      body: { ids, checked, ...(by ? { by } : {}) },
    })
  },

  bulkDelete(token: string, ids: string[]): Promise<DeletedResult> {
    return api<DeletedResult>(`/lists/${token}/items/delete`, { method: 'POST', body: { ids } })
  },

  reorder(token: string, ids: string[]): Promise<ItemsResult> {
    return api<ItemsResult>(`/lists/${token}/items/order`, { method: 'PUT', body: { ids } })
  },

  clearCompleted(token: string): Promise<DeletedResult> {
    return api<DeletedResult>(`/lists/${token}/clear-completed`, { method: 'POST' })
  },

  uncheckAll(token: string): Promise<{ changed: number; version: number }> {
    return api(`/lists/${token}/uncheck-all`, { method: 'POST' })
  },

  templates(lang: string): Promise<{ templates: Omit<Template, 'items'>[] }> {
    return api(`/templates?lang=${lang}`)
  },
}
